<template>
  <div>

    <!-- Search -->
    <div class="row mt-4">
      <div class="col-md-8">
        <input class="input form-control" @keypress.enter="find()" v-model="query.generic"
          placeholder="Pesquisar por CNPJ, AIT ou placa"
          v-tooltip.bottom="{ content: 'Pode pesquisar por multiplos valores separado por espaço' }">
        <search-icon @click="find()" class="icon icon-within" />
      </div>

      <button class="col-md-2 ml-2 btn-sm btn-outline" :class="{ 'btn-outline-active': showFilter }"
        @click="showFilter = !showFilter">
        Filtros
        <chevron-down-icon class="fa-angle-right" :class="{ 'rotate-90': showFilter }" />
      </button>

      <button v-if="changeQuery" @click="find()" class="btn btn-link">
        Aplicar filtros ({{ numberOfBadges }})
      </button>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters :query="query" @clearBadge="find()" @clearAllBadges="find()"
          :amount-changed="updateNumberOfBadges" :filters="badgesFilters" />
      </div>
    </div>

    <!-- Filters -->
    <template v-if="showFilter">
      <div class="row mt-4">
        <div class="col-md-4">
          <label>Empresas</label>
          <Select2 :options="filterCnpjs" v-model="query.cnpj" />
        </div>

        <div class="col-md-4">
          <label>Status da Infração</label>
          <Select2 :options="filterStatus" v-model="query.status" />
        </div>

        <div class="col-md-4">
          <label>Status da Empresa</label>
          <select v-model="query.companyStatus" class="browser-default custom-select" aria-label="Status da Empresa">
            <option :value="option.value" v-for="option in filterCompanyStatus.table" :key="option.label">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-6">
          <label>Órgão autuador</label>
          <Select2 :options="filterAutuador" v-model="query.autuador" />
        </div>

        <div class="col-md-6">
          <label>Alertas</label>
          <select v-model="query.messages" class="browser-default custom-select">
            <option :value="option.value" v-for="option in filterMessage.table" :key="option.label">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
            <option value="ocorrido_em">Data da infração</option>
            <option value="vencimento_em">Data de vencimento</option>
            <option value="boleto_vencimento_em">Data de vencimento do boleto</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>

      <!-- Custom Fields Filters -->
      <template v-if="customFields.length > 0">
        <div class="container-toggle-link mt-5">
          <a href="#" @click.prevent="toggleCustomFieldsFilters" class="toggle-link">
            {{ showCustomFieldsFilters ? 'ocultar' : 'mostrar' }} filtros de campos personalizados
          </a>
          <hr v-show="showCustomFieldsFilters">
        </div>

        <div v-show="showCustomFieldsFilters">
          <div class="row mt-4" v-for="(fieldsGroup, groupIndex) in customFieldsGroups" :key="groupIndex">
            <div v-for="field in fieldsGroup" :key="field.name" class="col-md-3">
              <label>{{ field.name }}</label>
              <template v-if="field.kind === 'select'">
                <Select2 :options="getCustomFieldFilter(field.name)" v-model="query[`customFields.${field.name}`]" />
              </template>
              <template v-else>
                <input type="text" class="form-control" v-model="query[`customFields.${field.name}`]"
                  placeholder="Digite a valor da pesquisa" />
              </template>
            </div>
          </div>
        </div>
      </template>
    </template>

    <hr>

    <!-- Buttons -->
    <div class="row mt-2">
      <div class="col-md-12">
        <button v-if="$withPolicy('cm-embarcador', 'consult')" class="btn btn-primary mt-1 mr-1"
          @click="showModal = true">
          Consulta avulsa
        </button>

        <button v-if="$canDownload('cm-embarcador', 'download_boleto', 'download_fici', 'download_demonstrativo', 'download_notification', 'download_process_views')"
          class="btn btn-primary mt-1 mr-1"
          @click="showDocumentExporter = true"
          :disabled="!someTicketSelected">
          Exportar documentos
        </button>

        <template v-if="$withPolicy('cm-embarcador', 'consult_history')">
          <router-link tag="button" to="/cm-embarcador/consultas" class="btn btn-primary mt-1 mr-1">
            Histórico de consultas
          </router-link>
        </template>

        <template v-if="getUser.brobotStaff">
          <router-link tag="button" to="/cm-embarcador/configuracoes" class="btn btn-primary mt-1 mr-1">
            Configurações
          </router-link>
        </template>

        <Dropdown title-dropdown="Relatório customizado" class="btn btn-primary mt-1 mr-1">
          <ul class="mb-1 mt-1">
            <router-link tag="li" to="/cm-embarcador/custom-reports" class="bkps-dropdown-item ml-2 mb-2">
              Gerenciar relatórios
            </router-link>

            <template v-if="customReports && customReports.length > 0">
              <template v-for="report in customReports">
                <li v-if="someTicketSelected" class="dropdown-item" @click="createCustomReport(report)" :key=report.id>
                  {{ report.name }}
                </li>
                <div v-else title="Selecione pelo menos um item" class="_disabled" :key="report.id + '_disabled'">
                  <li class="dropdown-item">
                    {{ report.name }}
                  </li>
                </div>
              </template>
            </template>
          </ul>
        </Dropdown>

        <button class="btn btn-primary mt-1 mr-1" @click="exportReport"
          :disabled="!someTicketSelected">
          Exportar relatório
        </button>

        <button class="btn btn-primary mt-1 mr-1" @click="showEditBatch = true"
          :disabled="!someTicketSelectedButNotAll">
          Editar em lote
        </button>

        <button class="btn btn-outline mt-1 float-right relative" @click="showSelectColumns = true">
          Mostrar colunas
        </button>

      </div>
    </div>

    <!-- Info -->
    <div class="row mt-4">
      <div class="col-md-12">
        <small>
          {{ infoTotalModels }}
          <template v-if="meta.lastConsult">
            Última consulta realizada em: {{ meta.lastConsult | datetime }}
          </template>
        </small>

        <button class="btn btn-outline ml-3 mb-1" v-if="tickets.length > 0"
          @click="changeCheck({ checkAll: !checkAll, sources: tickets })" :class="{ 'btn-outline-active': checkAll }">
          {{ !checkAll ? 'Selecionar todas' : 'Desmarcar todas' }} ({{ meta.totalCount || 'Calculando' }})
        </button>

        <div class="float-right" style="display: inline-flex;align-items: center;">
          <small class="mr-2" style="white-space: nowrap">Ordenar por:</small>
          <select v-model="orderBy" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
          </select>
        </div>

        <div class="float-right mr-2" style="display: inline-flex;align-items: center;">
          <small class="mr-2" style="white-space: nowrap">Exibir:</small>
          <select v-model="params.perPage" @change="find" class="browser-default custom-select">
            <option :value="10">10</option>
            <option :value="30">30</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row flex-fill d-flex justify-content-start">
      <div class="col-md-12 mt-2 table-col">
        <Table :tickets="tickets" :columns="selectedColumns" @changeCheckAllRow="changeCheckAllRow"
          @changeCheckRow="changeCheckRow" @fetchTickets="asyncTemplateSelect({ pagination: true })"
          :check-all-row="checkAllRow" />

        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination v-model="meta.currentPage" :total-count="meta.totalCount" :total-pages="meta.totalPages"
              @pagination="pagination" />
          </div>
        </div>

      </div>
    </div>

    <CompanyRequestModal v-model="showModal" appName="cm-embarcador" width="lg" />

    <!-- Modal selected columns -->
    <Modal v-model="showSelectColumns" width="lg">
      <div slot="header">
        Mostrar colunas
      </div>

      <div>
        <p>Selecione as colunas que deseja visualizar na tabela do CaçaMultas:</p>
      </div>

      <table class="columnsSelector table table-borderless table-border table-align">
        <thead>
          <tr>
            <th></th>
            <th>Nome da Coluna</th>
            <th>Descrição</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="column in sortedTableColumns" :key="column.key"
            @click="toggleColumn(column)" :style="column.locked ? '' : 'cursor: pointer;'">
            <td>
              <input type="checkbox" :id="column.key" v-model="column.checked" :disabled="column.locked"
                :class="column.locked ? '' : 'cursor-pointer'" />
            </td>
            <td v-if="column.isCustomField">
              <strong>{{ column.label }}</strong>
              <span title="Campo personalizado" class="ml-1 text-strong">
                <InfoIcon size="1.5x" />
              </span>
            </td>
            <td v-else>{{ column.label }}</td>
            <td>{{ column.description }}</td>
          </tr>
        </tbody>
      </table>
    </Modal>

    <!-- Modal confirm custom report -->
    <Modal v-model="showCustomReportConfirmModal" width="md">
      <div slot="header">
        Confirmar geração de relatório
      </div>

      <div>
        <p>Tem certeza que deseja gerar o relatório com {{ customReportConfirmData.count }} registros?</p>

        <div class="form-group mt-3">
          <label>Formato do relatório:</label>
          <div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" id="format-xlsx" value="xlsx"
                v-model="customReportConfirmData.format">
              <label class="form-check-label" for="format-xlsx">XLSX</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" id="format-csv" value="csv"
                v-model="customReportConfirmData.format">
              <label class="form-check-label" for="format-csv">CSV</label>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer">
        <button class="btn btn-outline mr-2" @click="showCustomReportConfirmModal = false">Cancelar</button>
        <button class="btn btn-primary" @click="confirmCustomReport">Confirmar</button>
      </div>
    </Modal>

    <EditBatch v-model="showEditBatch" :tickets="selectedTickets" :custom-fields="customFields" @ticketsSaved="find" />

    <DocumentExporterModal
      v-model="showDocumentExporter"
      :selected-tickets="selectedTickets"
      :check-all="checkAll"
      :total-count="meta.totalCount"
      :build-params="buildParams"
    />
  </div>
</template>

<script>
import BadgeFilters from '@/components/shares/BadgeFilters'
import CheckedModels from '@/mixins/CheckedModels'
import EntityProxy from '@/models/EntityProxy'

import Dropdown from '../shares/specialTag/Dropdown'
import GetAccount from '@/mixins/GetAccount.vue'
import GetUser from '@/mixins/GetUser.vue'
import Formatter from '@/models/Formatter'

import Modal from '../shares/specialTag/Modal'
import ModelMapper from '@/mixins/ModelMapper'
import Pagination from '@/components/shares/specialTag/Pagination'

import Select2 from '@/components/shares/Select2'
import Table from '@/components/cm-embarcador/Table'
import CompanyRequestModal from '@/components/cm-embarcador/CompanyRequestModal'
import EditBatch from '@/components/cm-embarcador/EditBatch'
import DocumentExporterModal from '@/components/cm-embarcador/DocumentExporterModal'

import handleSearchable from '@/models/handleSearchable'
import Sorter from '@/models/Sorter'

import { ChevronDownIcon, InfoIcon, SearchIcon } from 'vue-feather-icons'

import { IBGE } from '@/models/ibge'
import { Filter } from '@/models/filter'
import { IssuingAuthority } from '@/models/IssuingAuthority'
import { COMPANY_STATUS, COMPANY_STATUS_FILTER_TABLE, companyStatusRowLabel } from '@/utils/companyStatus'

const keyTemplateColumns = 'cmEmbarcadorticketTemplateColumns'
const keyColumnsMigrations = 'cmEmbarcadorticketTemplateColumnsMigrations'
const fiveYearsInMinutes = 60 * 24 * 30 * 12 * 5

const columnsMigrations = {
  '77754': ['company.name']
}

const cityHallName = (cityCode) => cityCode && IBGE.getByCityCode(cityCode)?.name
const authority = (value) => IssuingAuthority.label(value)
// const authorities = (value) => value && value.map(v => authority(v)).join(' + ')
const translateStatus = value => {
  return {
    '0': 'Sem status',
    '100': 'Em aberto',
    '200': 'Vencido',
    '300': 'Prescrito',
    '400': 'Sem informação',
    '500': 'Pago',
    '600': 'Cancelado',
  }[value]
}

const tableColumns = [
  { key: 'ait', label: 'AIT', checked: true, description: 'Número do auto de infração' },
  { key: 'placa', label: 'Placa', checked: true, description: 'Placa do veículo' },
  { key: 'renavam', label: 'Renavam', checked: true, description: 'Número do Renavam (Registro Nacional de Veículos Automotores)' },
  { key: 'cnpj', label: 'CNPJ', checked: true, method: Formatter.cnpj, description: 'Número do CNPJ (Cadastro Nacional da Pessoa Jurídica)' },
  { key: 'company.name', label: 'Razão Social', checked: true, description: 'Razão social cadastrada em Empresas para o CNPJ da multa' },
  { key: 'numeroProcesso', label: 'Número do processo', checked: true, description: 'Número do processo' },
  { key: 'status', label: 'Status da Infração', checked: true, method: translateStatus, description: 'Status da infração' },
  { key: 'companyStatus', label: 'Status da Empresa', checked: true, locked: true, method: companyStatusRowLabel, description: 'Status da empresa do processo (ativa, inativa ou excluída)' },
  { key: 'situacao', label: 'Situação', checked: true, description: 'Situação ou condição' },
  { key: 'ocorridoEm', label: 'Data da Infração', checked: true, method: Formatter.stringToDatetime, description: 'Data da infração' },
  { key: 'vencimentoEm', label: 'Vencimento da Infração', checked: true, method: Formatter.stringToDate, description: 'Data do vencimento da infração' },
  { key: 'lastBoleto.vencimentoEm', label: 'Vencimento do Boleto', checked: true, method: Formatter.stringToDate, description: 'Data do vencimento do Boleto' },
  { key: 'lastBoleto.codigoDeBarra', label: 'Código de barra', checked: true, description: 'Código de barra do Boleto' },
  { key: 'valorCents', label: 'Valor', checked: true, method: Formatter.centsToPrice, description: 'Valor da infração' },
  { key: 'valorEstimadoCents', label: 'Valor estimado', checked: false, method: Formatter.centsToPrice, description: 'Valor estimado da infração' },
  { key: 'lastBoleto.valorCents', label: 'Valor do Boleto', checked: true, method: Formatter.centsToPrice, description: 'Valor do Boleto' },
  { key: 'lastBoleto.nossoNumeroBoleto', label: 'Nosso Número do Boleto', checked: false, description: 'Nosso Número do Boleto (ANTT, ANTT Processos, DER/PR, DNIT)' },
  { key: 'valorPagoCents', label: 'Valor Pago', checked: true, method: Formatter.centsToPrice, description: 'Valor pago (informação retornada pelo órgão consultado, porém nem todos os órgãos retornam esta informação)' },
  { key: 'autuador', label: 'Órgão autuador', checked: true, description: 'Órgão autuador', method: authority },
  // { key: 'orgaoConsultado', label: 'Órgão consultado', checked: false, method: authority, description: 'Órgão consultado' },
  { key: 'codigoMulta', label: 'Código', checked: true, description: 'Código da infração' },
  { key: 'descricao', label: 'Descrição', checked: true, description: 'Descrição da infração' },
  { key: 'local', label: 'Local', checked: true, description: 'Local da infração' },
  { key: 'codigoDaCidade', label: 'Cidade', checked: false, method: cityHallName, description: 'Cidade' },
  { key: 'uf', label: 'UF', checked: true, description: 'Unidade federativa' },
  // { key: 'points', label: 'Pontuação', checked: true, description: 'Pontuação' },
  { key: 'createdAt', label: 'Criado em', checked: true, method: Formatter.stringToDatetime, description: 'Criado em' },
  { key: 'updatedAt', label: 'Atualizado em', checked: true, method: Formatter.stringToDatetime, description: 'Atualizado em' },
]

export default {
  components: {
    BadgeFilters, ChevronDownIcon, Dropdown,
    CompanyRequestModal, EditBatch, Pagination,
    SearchIcon, Select2, Table,
    Modal, InfoIcon, DocumentExporterModal
  },
  computed: {
    currentAxios() {
      return this.axios.v3
    },
    badgesFilters() {
      const baseFilters = [
        this.filterIssuingAuthority,
        this.filterCnpjs,
        this.filterStatus,
        this.filterEndDate,
        this.filterStartDate,
        this.filterAutuador,
        this.filterCompanyStatus,
        this.filterMessage
      ]

      const customFieldsFilters = Object.values(this.customFieldFilters)

      return [...baseFilters, ...customFieldsFilters]
    },
    someTicketSelected() {
      return this.checkAll || this.tickets.some(ticket => ticket.checked)
    },
    someTicketSelectedButNotAll() {
      return !this.checkAll && this.tickets.some(ticket => ticket.checked)
    },
    tickets() {
      return this.getModels || []
    },
    selectedColumns() {
      return this.tableColumns.filter(column => column.checked)
    },
    enableDownloadCustomReport() {
      return this.checkAll || (this.tickets.length > 0 && this.tickets.some(item => item.checked))
    },
    selectedTickets() {
      return this.tickets.filter(ticket => ticket.checked)
    },
    customFieldsGroups() {
      const groups = []
      for (let i = 0; i < this.customFields.length; i += 4) {
        groups.push(this.customFields.slice(i, i + 4))
      }
      return groups
    },
    sortedTableColumns() {
      return Sorter.sortBy(this.tableColumns, 'label')
    }
  },
  created() {
    this.$set(this.params, 'perPage', 10)
    this.order = ['updated_at', 'desc']
    this.methods = ['last_boleto', 'custom_fields', 'company']
    this.$set(this.query, 'periodType', 'updated_at')
    this.$set(this.query, 'messages', '')
    this.$set(this.query, 'companyStatus', this.companyStatusFromUrl())
    this.defaultModelSetting.updateQueryURL = true

    this.asyncTemplateSelect({
      byURL: true,
      pagination: true,
      finish: () => {
        this.loadFilters()
        this.findCustomReports()
      }
    })

    this.initializersTableColumns()
  },
  data: () => ({
    customReports: null,
    tableColumns: tableColumns,
    customFields: [],
    customFieldFilters: {},
    showCustomFieldsFilters: false,
    filterCnpjs: null,
    filterIssuingAuthority: null,
    filterEndDate: new Filter({
      key: 'endDate',
      label: 'Data final',
      type: 'date'
    }),
    filterStartDate: new Filter({
      key: 'startDate',
      label: 'Data inicial',
      type: 'date'
    }),
    filterStatus: new Filter({
      key: 'status',
      label: 'Status da Infração',
      table: [
        // ['sem status', '0'],
        ['em aberto', '100'],
        ['vencido', '200'],
        ['prescrito', '300'],
        ['sem informação', '400'],
        ['pago', '500'],
        ['cancelado', '600'],
      ]
    }),
    filterAutuador: new Filter({
      key: 'autuador',
      label: 'Órgãos Autuadores',
      table: [
        ['DNIT', '000300'],
        ['ANTT', '000400'],
        ['DER/PR', '116200'],
        ['DER/SP', '126200'],
      ]
    }),
    filterCompanyStatus: new Filter({
      key: 'companyStatus',
      label: 'Status da Empresa',
      table: COMPANY_STATUS_FILTER_TABLE
    }),
    filterMessage: new Filter({
      key: 'messages',
      label: 'Alertas',
      table: [
        ['Todas', ''],
        ['Com alerta',  '$present'],
        ['Sem alerta',  '$not_present']
      ]

    }),
    modelPathPrefix: 'cm-embarcador',
    modelPath: 'multas',
    nameModel: 'Ticket',
    showFilter: false,
    showModal: false,
    showSelectColumns: false,
    showCustomReportConfirmModal: false,
    showEditBatch: false,
    showDocumentExporter: false,
    customReportConfirmData: {
      report: null,
      count: 0,
      format: 'xlsx'
    },
    orderBy: 'updated_at'
  }),
  filters: {
    authority(code) {
      return IssuingAuthority.label(code)
    }
  },
  methods: {
    companyStatusFromUrl() {
      // O dashboard manda `q[company_status]` vazio quando está em "Todas", e o
      // setQueryByUrl descarta valor vazio — por isso o default cede aqui, e só
      // quando a URL não fala do filtro.
      const params = this.$utilUri.getQuery({ camelcase: false })
      const query = params.query || params.q || {}

      return query.company_status === undefined ? String(COMPANY_STATUS.ENABLED) : query.company_status
    },
    toggleColumn(column) {
      if (column.locked)
        return

      column.checked = !column.checked
      this.updateColumnsInLocalStorage()
    },
    initializersTableColumns() {
      const keys = this.applyColumnsMigrations(this.$ls.get(keyTemplateColumns))
      if (!keys)
        return

      // ponytail: aceita a key antiga lastBankSlip.* salva antes do rename (#74054),
      // senao quem personalizou colunas perde as 4 de boleto. Remover apos alguns meses.
      this.tableColumns.forEach(column => {
        if (column.locked)
          return

        column.checked = keys.includes(column.key) || keys.includes(column.key.replace('lastBoleto.', 'lastBankSlip.'))
      })
    },
    applyColumnsMigrations(keys) {
      const applied = this.$ls.get(keyColumnsMigrations) || []
      const pending = Object.keys(columnsMigrations).filter(id => !applied.includes(id))
      if (pending.length === 0)
        return keys

      this.$ls.set(keyColumnsMigrations, [...applied, ...pending], { minutes: fiveYearsInMinutes })
      if (!keys)
        return keys

      const newKeys = [...new Set([...keys, ...pending.flatMap(id => columnsMigrations[id])])]
      this.$ls.set(keyTemplateColumns, newKeys, { minutes: fiveYearsInMinutes })
      return newKeys
    },
    toggleCustomFieldsFilters() {
      this.showCustomFieldsFilters = !this.showCustomFieldsFilters
    },
    changeCheckAllRow(value) {
      this.changeCheck({ checkAllRow: value, sources: this.tickets })
    },
    changeCheckRow() {
      this.changeCheck({ sources: this.tickets })
    },
    buildParams(pagination) {
      if (this.query.generic)
        this.query.generic = handleSearchable(this.query.generic)

      return ModelMapper.methods.buildParams.call(this, pagination)
    },
    find() {
      this.asyncTemplateSelect()
    },
    async loadFilters() {
      const methods = [
        this.loadCustomFields,
        this.loadCompanies,
      ]

      for (const method of methods)
        await method()
    },
    loadCustomFields() {
      return this.axios.v3.get('/custom_fields?q[resource]=cm-embarcador/multa&meta=false&per_page=100000')
        .then(response => this.customFields = EntityProxy.build(response.data))
        .then(() => this.$ls.set('cm-embarcador/multas/custom-fields', this.customFields, { minutes: 1 }))
        .then(() => this.updateColumnsWithCustomFields())
        .then(() => this.createCustomFieldFiltes())
    },
    updateColumnsWithCustomFields() {
      this.customFields.forEach(t => {
        const key = `customFields.${t.name}`
        if (this.tableColumns.find(c => c.key === key)) return

        this.tableColumns.push({
          key: key,
          label: t.name,
          checked: false,
          description: t.description,
          isCustomField: true
        })
      })
      this.initializersTableColumns()
    },
    createCustomFieldFiltes() {
      this.customFields.forEach(field => {
        if (field.kind === 'select') {
          const options = [['Nenhum', '$none'], ...(field.values || []).map(value => [value, value])]
          this.customFieldFilters[field.name] = new Filter({
            key: `customFields.${field.name}`,
            label: field.name,
            table: options
          })
        } else {
          this.customFieldFilters[field.name] = new Filter({
            key: `customFields.${field.name}`,
            label: field.name,
            type: 'input'
          })
        }
      })
    },
    getCustomFieldFilter(fieldName) {
      return this.customFieldFilters[fieldName] || null
    },
    loadCompanies() {
      const params = {
        q: {
          type: ['parent', 'subsidiary'],
        },
        per_page: 10000,
        meta: false
      }

      return this.axios.get('/companies', { params })
        .then(response => response.data)
        .then((companies) => this.buildFilterCnpj(companies))
    },
    buildFilterCnpj(companies) {
      const cnpjs = companies.map(company => [
        company.name + ' - ' + Formatter.cnpj(company.cnpj),
        company.cnpj
      ])

      this.filterCnpjs = new Filter({
        key: 'cnpj',
        label: 'CNPJ',
        table: cnpjs
      })
    },
    loadIssuingAuthorities() {
      return this.axios.get('/cm-embarcador/tickets/fetch_column/issuing_authority')
        .then(response => {
          this.filterIssuingAuthority = new Filter({
            key: 'issuingAuthority',
            label: 'Órgãos Autuadores',
            table: response.data.map(code => [IssuingAuthority.label(code), code])
          })
        })
    },
    getDocuments(kind) {
      const params = this.checkAll ?
        this.buildParams() :
        { q: { id: this.tickets.filter(c => c.checked).map(c => c.id) } }
      params.q.kind = kind

      this.openLoad()
      this.axios.get('cm-embarcador/tickets/latest_documents', { params })
        .then(response => {
          if (response.data.url)
            this.$parse.downloadByLink(response.data.url)
          else if (response.data)
            this.$notify.add(response.meta.notice)
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => this.closeLoad())
    },
    updateColumnsInLocalStorage() {
      const selectedKeys = [...new Set(this.tableColumns.filter(e => e.checked).map(e => e.key))]
      this.$ls.set(keyTemplateColumns, selectedKeys, { minutes: fiveYearsInMinutes })
    },
    findCustomReports(forceLoad) {
      const customReports = this.$ls.get('cm-embarcador/custom-reports')
      if (!forceLoad && customReports) {
        this.customReports = EntityProxy.build(customReports)
        this.lastFetch = this.$ls.get('cm-embarcador/custom-reports-last-fetch')
        return
      }

      this.openLoad()
      this.axios.v3.get('custom_reports')
        .then(response => {
          this.customReports = EntityProxy.build(response.data)
          this.$ls.set('cm-embarcador/custom-reports', this.customReports, { minutes: 1 })
          this.lastFetch = `Última atualização: ${new Date().toLocaleString()}`
          this.$ls.set('cm-embarcador/custom-reports-last-fetch', this.lastFetch, { minutes: 1 })
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => this.closeLoad())
    },
    createCustomReport(report) {
      let count = null
      let params = null

      if (this.checkAll) {
        params = this.buildParams()
        count = this.meta.totalCount
      } else {
        const ids = this.tickets.filter(c => c.checked).map(c => c.id)
        params = { q: { id: ids } }
        count = ids.length
      }

      this.customReportConfirmData = {
        report,
        count,
        params,
        format: 'xlsx'
      }
      this.showCustomReportConfirmModal = true
    },
    exportReport() {
      let params
      let amount

      if (this.checkAll) {
        params = this.buildParams()
        amount = this.meta.totalCount
      } else {
        const ids = this.selectedTickets.map(t => t.id)
        params = { q: { id: ids } }
        amount = ids.length
      }

      if (!confirm(`Exportar ${amount} Multa(s).\nTem certeza?`))
        return

      this.openLoad()
      this.currentAxios.post('cm-embarcador/multas/report', this.$parse.toSnakecase(params))
        .then(response => {
          if (response.data && response.data.link)
            this.$parse.downloadByLink(response.data.link)
          if (response.meta && response.meta.notice)
            this.$notify.add(response.meta.notice)
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => this.closeLoad())
    },
    confirmCustomReport() {
      const { report, params, format } = this.customReportConfirmData
      this.showCustomReportConfirmModal = false
      this.openLoad()

      this.currentAxios.post(`cm-embarcador/multas/custom-reports/${report.id}`, {
        ...params,
        format
      })
        .then(response => {
          if (response.data && response.data.link)
            this.$parse.downloadByLink(response.data.link)
          else if (response.meta.notice)
            this.$notify.add(response.meta.notice)
          else if (response.errors && response.errors.length > 0)
            response.errors.forEach(e => this.$notify.addError(e))
          else
            this.$notify.addError('Falha ao gerar relatório.')
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => {
          this.closeLoad()
          this.changeCheck({ checkAll: false, sources: this.tickets })
        })
    },
  },
  mixins: [ModelMapper, CheckedModels, GetAccount, GetUser],
  name: 'Index',
  watch: {
    tickets(newValue, oldValue) {
      if (newValue !== oldValue)
        this.changeCheck({ checkAllRow: false })
    },
    showDocumentExporter(newValue) {
      if (!newValue)
        this.changeCheck({ checkAll: false, sources: this.tickets })
    },
    orderBy(newValue, oldValue) {
      if (newValue === oldValue) return

      this.order = []
      this.addOrderBy(newValue, 'desc')
      this.find()
    },
  }
}

</script>

<style scoped>
._disabled {
  opacity: 0.5;
  cursor: default;
}

.container-toggle-link {
  position: relative;
}

.toggle-link {
  position: absolute;
  right: 0;
  top: -20px;
  font-size: 0.9em;
  color: #007bff;
  text-decoration: none;
}

.toggle-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.table-col {
  min-width: 0;
}
</style>
